import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Terminal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/App";
import { useEffect, useState, useRef } from "react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxjeWJlcnB1bmslMjBuZW9uJTIwY2l0eSUyMGFpJTIwZnV0dXJpc3RpYyUyMGFzc2lzdGFudHxlbnwwfHx8fDE3OTI5NTg5MjB8MA&ixlib=rb-4.1.0&q=85";
const CROWD_IMG = "https://images.unsplash.com/photo-1732704895089-ec0e78d7ce91?q=80&w=1600&auto=format&fit=crop";

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className={`block overflow-hidden pb-1 ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

function DialogueBox({ lang, t, audioEnabled }) {
    const [currentLine, setCurrentLine] = useState(0);
    const [displayedText, setDisplayedText] = useState("");
    const [isTyping, setIsTyping] = useState(true);
    const dialogue = t.hero.dialogue;
    const totalLines = dialogue.david.length + dialogue.lucy.length;
    const audioRef = useRef(null);

    useEffect(() => {
        const isDavid = currentLine % 2 === 0;
        const lineIndex = Math.floor(currentLine / 2);
        const text = isDavid ? dialogue.david[lineIndex] : dialogue.lucy[lineIndex];
        
        if (!text) return;
        
        // Play audio for current line
        if (audioEnabled) {
            const speaker = isDavid ? "david" : "lucy";
            const audioSrc = `/audio/dialogue/${speaker}-${lang}-${lineIndex + 1}.mp3`;
            const audio = new Audio(audioSrc);
            audioRef.current = audio;
            audio.play().catch(() => {});
        }
        
        let charIndex = 0;
        setDisplayedText("");
        setIsTyping(true);
        
        const typeInterval = setInterval(() => {
            if (charIndex < text.length) {
                setDisplayedText(text.slice(0, charIndex + 1));
                charIndex++;
            } else {
                setIsTyping(false);
                clearInterval(typeInterval);
                setTimeout(() => {
                    setCurrentLine((prev) => (prev + 1) % totalLines);
                }, 2000);
            }
        }, 40);
        
        return () => {
            clearInterval(typeInterval);
            audioRef.current?.pause();
        };
    }, [currentLine, dialogue, totalLines, lang, audioEnabled]);

    const isDavid = currentLine % 2 === 0;
    const speaker = isDavid
        ? (lang === "ua" ? "Девід" : "David")
        : (lang === "ua" ? "Люсі" : "Lucy");
    const speakerColor = isDavid ? "text-[#fcee0a]" : "text-[#ff4bd8]";

    return (
        <div className="bg-[#080a12]/85 backdrop-blur border border-[#212b45] rounded-xl p-4 min-h-[80px]">
            <div className={`font-mono2 text-[9px] tracking-[0.25em] ${speakerColor} mb-2 flex items-center gap-2`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current pulse-dot" />
                {speaker}
                {audioEnabled && (
                    <span className="ml-auto flex items-end gap-0.5 h-3" data-testid="dialogue-audio-indicator">
                        {[0, 1, 2].map((i) => (
                            <span key={i} className="eq-bar w-[3px] h-2.5 bg-current/80 rounded-sm" style={{ animationDelay: `${i * 0.12}s` }} />
                        ))}
                    </span>
                )}
            </div>
            <div className="text-sm text-slate-200 leading-relaxed min-h-[40px]">
                {displayedText}
                {isTyping && <span className="cursor-blink">▌</span>}
            </div>
        </div>
    );
}

export default function Hero() {
    const { t, lang, speak } = useLang();
    const navigate = useNavigate();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 20 });
    const sy = useSpring(my, { stiffness: 60, damping: 20 });
    const orbX = useTransform(sx, (v) => v * 46);
    const orbY = useTransform(sy, (v) => v * 46);
    const orb2X = useTransform(sx, (v) => v * -30);
    const orb2Y = useTransform(sy, (v) => v * -30);
    const charX = useTransform(sx, (v) => v * -20);
    const charY = useTransform(sy, (v) => v * -12);
    const { scrollY } = useScroll();
    const bgYRaw = useTransform(scrollY, [0, 900], [0, 120]);
    const bgY = useSpring(bgYRaw, { stiffness: 55, damping: 18, mass: 0.6 });
    const [dialogueAudio, setDialogueAudio] = useState(false);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden pt-16"
            onMouseMove={(e) => {
                mx.set(e.clientX / window.innerWidth - 0.5);
                my.set(e.clientY / window.innerHeight - 0.5);
            }}
        >
            <motion.div
                aria-hidden="true"
                data-testid="hero-bg-parallax"
                className="absolute inset-0 bg-cover bg-center opacity-[0.07] lg:opacity-[0.1]"
                style={{ backgroundImage: `url(/char/maine-bg.jpg)`, scale: 1.18, y: bgY }}
            />
            <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${HERO_IMG})` }} />
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.16] mix-blend-screen"
                style={{ backgroundImage: `url(${CROWD_IMG})` }}
            />
            <div className="absolute inset-0 speedlines pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#080a12]/70 via-[#080a12]/55 to-[#080a12]" />
            <div className="absolute bottom-0 left-0 right-0 h-[42vh] grid-floor opacity-60" />
            <motion.div className="absolute -left-32 top-1/4 w-[420px] h-[420px] rounded-full bg-[#49f5e8]/14 blur-[110px]" style={{ x: orbX, y: orbY }} />
            <motion.div className="absolute right-[-140px] top-10 w-[460px] h-[460px] rounded-full bg-[#ff4bd8]/12 blur-[120px]" style={{ x: orb2X, y: orb2Y }} />

            {/* 30s video background scene */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="hero-video-bg">
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster="/videos/david-lucy-poster.jpg"
                    className="absolute inset-0 w-full h-full object-cover opacity-[0.12] mix-blend-screen"
                >
                    <source src="/videos/david-lucy-30s.mp4" type="video/mp4" />
                </video>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 1 }}
                className="kana kana-vertical hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 text-lg text-[#49f5e8]/50 select-none"
                aria-hidden="true"
            >
                ネオンAI・ナイトシティ
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="kana kana-watermark absolute right-[30%] top-[8%] text-[9rem] xl:text-[12rem] leading-none hidden lg:block"
                aria-hidden="true"
            >
                ネオン
            </motion.div>

            <motion.div
                initial={{ opacity: 0, x: 90 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="hidden lg:block absolute right-0 top-16 bottom-0 w-[44%] pointer-events-none"
                data-testid="hero-character"
            >
                <motion.div style={{ x: charX, y: charY }} className="relative w-full h-full">
                    <img
                        src="/char/hero-lucy.png"
                        alt="Lucy — netrunner companion"
                        className="char-bleed absolute right-0 bottom-0 max-h-[94%] object-contain object-bottom"
                    />
                    <div className="script-pink absolute left-[8%] top-[24%] text-4xl -rotate-6">
                        {lang === "ua" ? "Люсі" : "Lucy"}
                    </div>
                    <div className="halftone absolute right-[6%] top-[10%] w-32 h-32 opacity-70" aria-hidden="true" />
                    <div className="absolute left-[2%] bottom-[16%] bg-[#080a12]/85 backdrop-blur border border-[#fcee0a]/50 px-3 py-2 font-mono2 text-[9px] tracking-[0.25em]">
                        <span className="edg-text">NETRUNNER_077</span>
                        <span className="text-slate-500"> // EDGE CLASS</span>
                        <span className="kana block text-[10px] text-[#fcee0a]/80 mt-1 tracking-[0.3em]">ネットランナー</span>
                    </div>
                    <div className="absolute right-[4%] top-[6%] warning-stripes w-14 h-2.5 opacity-90" />
                </motion.div>
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15, duration: 0.8 }}
                    className="flex items-center gap-3 font-mono2 text-[11px] sm:text-xs tracking-[0.3em] text-[#49f5e8]"
                    data-testid="hero-kicker"
                >
                    <span className="w-2 h-2 bg-[#49f5e8] pulse-dot" />
                    {t.hero.kicker}
                    <span className="hidden sm:inline text-[#fcee0a] border border-[#fcee0a]/50 px-2 py-0.5">EDGERUNNERS</span>
                    <span className="kana-chip hidden md:inline">アニメ</span>
                </motion.div>

                <h1 className="font-display font-black uppercase leading-[0.98] tracking-wide mt-6 text-4xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-3xl">
                    <MaskedLine delay={0.25}>{t.hero.line1}</MaskedLine>
                    <MaskedLine delay={0.4}>
                        {t.hero.line2a}{" "}
                        <span className="neon-text glitch flicker" data-text={t.hero.line2b}>
                            {t.hero.line2b}
                        </span>
                    </MaskedLine>
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.65, duration: 0.8 }}
                    className="script-neon text-2xl sm:text-3xl mt-5 -rotate-1"
                    data-testid="hero-script-line"
                >
                    {t.hero.scriptLine}
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-5 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed"
                    data-testid="hero-subtitle"
                >
                    {t.hero.sub}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.95, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-9 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-cta-chat"
                        onClick={() => navigate("/chat")}
                        className="group flex items-center gap-3 bg-[#d7ff35] text-[#080a12] font-bold text-sm px-7 py-4 rounded-full hover:shadow-[0_0_38px_rgba(215,255,53,0.55)] transition-shadow"
                    >
                        <Terminal size={16} />
                        {t.hero.ctaChat}
                        <ArrowDown size={15} className="group-hover:translate-y-0.5 transition-transform" />
                    </button>
                    <button
                        data-testid="hero-cta-download"
                        onClick={() => navigate("/android")}
                        className="px-7 py-4 rounded-full border border-[#fcee0a]/60 text-[#fcee0a] font-mono2 text-xs tracking-[0.18em] uppercase hover:bg-[#fcee0a]/10 hover:border-[#fcee0a] transition-colors"
                    >
                        {t.hero.ctaApk}
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.15, duration: 1 }}
                    className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono2"
                    data-testid="hero-stats"
                >
                    {t.hero.stats.map((s) => (
                        <div key={s.k}>
                            <div className="text-[9px] tracking-[0.3em] text-slate-500">{s.k}</div>
                            <div className="text-sm text-[#d7ff35] mt-1 tracking-wider">{s.v}</div>
                        </div>
                    ))}
                </motion.div>

                {/* David & Lucy dialogue */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2, duration: 1 }}
                    className="mt-10 max-w-lg"
                    data-testid="hero-dialogue"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <button
                            data-testid="dialogue-audio-toggle"
                            onClick={() => setDialogueAudio(!dialogueAudio)}
                            className={`font-mono2 text-[9px] tracking-[0.2em] rounded-full border px-3.5 py-1.5 transition-colors ${
                                dialogueAudio
                                    ? "border-[#ff4bd8] text-[#ff4bd8] bg-[#ff4bd8]/10 shadow-[0_0_14px_rgba(255,75,216,0.3)]"
                                    : "border-[#2c3a5c] text-slate-400 hover:border-[#ff4bd8]/60 hover:text-[#ff4bd8]"
                            }`}
                        >
                            {dialogueAudio ? "🔊 ОЗВУЧКА УВІМК" : "🔇 ОЗВУЧКА ВИМК"}
                        </button>
                    </div>
                    <DialogueBox lang={lang} t={t} audioEnabled={dialogueAudio} />
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono2 text-[9px] tracking-[0.4em] text-slate-600 flex flex-col items-center gap-2"
            >
                SCROLL
                <span className="w-px h-8 bg-gradient-to-b from-[#d7ff35] to-transparent" />
            </motion.div>
            <div className="absolute bottom-0 left-0 right-0 h-[7px] warning-stripes opacity-70" />
        </section>
    );
}
